import { TFunction } from 'i18next';
import { CloudIcon, LightbulbIcon, UsersIcon } from 'lucide-react';

import { FilterDefinition } from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { FilterOption } from '@/components/ui/custom/ComplexFilter/types';
import type { Member } from '@/shared/types/member';

export const CREATOR_FILTER = 'createdBy';
export const STATUS_FILTER = 'status';
export const PROVIDER_FILTER = 'provider';

/** One option per distinct value in use, sorted by label, so no option matches nothing. */
const distinctOptions = <T>(
  items: T[],
  getValue: (item: T) => string | null | undefined,
  getLabel: (value: string) => string
): FilterOption[] =>
  [...new Set(items.map(getValue).filter(Boolean) as string[])]
    .map((value) => ({ value, label: getLabel(value) }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const creatorFilterDefinition = (
  items: { created_by: string | null }[],
  getMember: (userId: string) => Member | undefined,
  t: TFunction
): FilterDefinition => ({
  id: CREATOR_FILTER,
  label: t('common.created_by'),
  icon: UsersIcon,
  type: 'checkbox',
  options: distinctOptions(
    items,
    (item) => item.created_by,
    (userId) => getMember(userId)?.name || userId
  ),
});

export const providerFilterDefinition = <P extends string>(
  items: { provider: P }[],
  getLabel: (provider: P) => string,
  t: TFunction
): FilterDefinition => ({
  id: PROVIDER_FILTER,
  label: t('developer_tools.language_models_provider'),
  icon: CloudIcon,
  type: 'checkbox',
  options: distinctOptions(
    items,
    (item) => item.provider,
    (provider) => getLabel(provider as P)
  ),
});

/** Item values are `'active'` and `'inactive'`. */
export const statusFilterDefinition = (t: TFunction): FilterDefinition => ({
  id: STATUS_FILTER,
  label: t('common.status'),
  icon: LightbulbIcon,
  type: 'radio',
  options: [
    { label: t('common.active'), value: 'active' },
    { label: t('common.inactive'), value: 'inactive' },
  ],
});
